import type { Session } from '@supabase/supabase-js';
import { requireSession, signOut } from '../lib/auth-guard';
import { supabase } from '../lib/supabase';
import { initEditMap } from '../lib/mapbox-helpers';
import {
  DirtyTracker,
  PRICE_RANGES,
  COMMON_LANGUAGES,
  TAGLINE_MAX,
  parseOperatingHours,
  weeklyHoursToDisplayMap,
  type EditableProfileFields,
} from '../lib/fields';
import { mountHoursPicker, validateWeeklyHours } from '../lib/hours-picker';
import { initMediaSections, toStringArray, toMediaTypes } from '../lib/media-ui';
import { mountMenuSection } from '../lib/menu-ui';
import { isFoodType } from '../lib/menu-api';
import { mountPerTypeSection } from '../lib/per-type-ui';
import { PER_TYPE_COLUMNS, type PerTypeProfile } from '../lib/per-type';

type ProviderProfileRow = {
  id: string;
  user_id: string;
  business_name: string;
  business_type: string | null;
  tagline: string | null;
  description: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  instagram_handle: string | null;
  facebook_handle: string | null;
  tiktok_handle: string | null;
  address: string | null;
  operating_hours: unknown;
  price_range: string | null;
  languages: unknown;
  latitude: number | null;
  longitude: number | null;
  city_id: string | null;
  logo_url: string | null;
  banner_url: string | null;
  cover_image_url: string | null;
  banner_align_x: number | null;
  banner_align_y: number | null;
  media_urls: unknown;
  media_types: unknown;
};

const FLORENCE_CENTROID: [number, number] = [11.2558, 43.7696];

const PROFILE_COLUMNS = [
  'id', 'user_id', 'business_name', 'business_type',
  'tagline', 'description',
  'email', 'phone', 'website',
  'instagram_handle', 'facebook_handle', 'tiktok_handle',
  'address', 'operating_hours', 'price_range', 'languages',
  'latitude', 'longitude', 'city_id',
  'logo_url', 'banner_url', 'cover_image_url',
  'banner_align_x', 'banner_align_y', 'media_urls', 'media_types',
].join(', ');

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
};
function esc(s: string | null | undefined): string {
  return (s ?? '').replace(/[&<>"']/g, (c) => HTML_ESCAPES[c] ?? c);
}

function normalizeLanguages(raw: unknown): string[] | null {
  if (!Array.isArray(raw)) return null;
  const out = raw.filter((x): x is string => typeof x === 'string');
  return out.length === 0 ? null : out;
}

export async function renderEdit(container: HTMLElement): Promise<void> {
  let session: Session | null = null;
  try {
    session = await requireSession();
  } catch {
    return;
  }
  if (!session) return;

  // Note: provider_profiles has TWO SELECT RLS policies (owner-scoped AND
  // "Public read access for authenticated users" with qual: true).
  // PostgreSQL OR-combines permissive policies, so a bare SELECT returns ALL
  // rows. We must explicitly scope by user_id to make .single() resolve to
  // one row. RLS still gates UPDATE/DELETE — defence in depth, not relied on
  // here for SELECT narrowing.
  const { data: profile, error } = await supabase
    .from('provider_profiles')
    .select(PROFILE_COLUMNS)
    .eq('user_id', session.user.id)
    .single<ProviderProfileRow>();

  if (error || !profile) {
    renderNoProfile(container);
    return;
  }

  if (profile.user_id.toLowerCase() !== session.user.id.toLowerCase()) {
    console.error('[edit] profile.user_id mismatch — RLS may be misconfigured.', {
      profileUid: profile.user_id,
      sessionUid: session.user.id,
    });
    await signOut();
    return;
  }

  container.innerHTML = renderShell(profile);

  const mapEl = container.querySelector<HTMLDivElement>('#map');
  const coordsDisplay = container.querySelector<HTMLElement>('#coords-display');
  const dirtyFlag = container.querySelector<HTMLElement>('#dirty-flag');
  const saveBtn = container.querySelector<HTMLButtonElement>('#save-btn');
  const signoutBtn = container.querySelector<HTMLButtonElement>('#signout-btn');
  const fieldsCol = container.querySelector<HTMLElement>('.edit-fields-col');
  if (!mapEl || !coordsDisplay || !dirtyFlag || !saveBtn || !signoutBtn || !fieldsCol) return;

  const tracker = new DirtyTracker<EditableProfileFields>({
    tagline: profile.tagline,
    description: profile.description,
    email: profile.email,
    phone: profile.phone,
    website: profile.website,
    instagram_handle: profile.instagram_handle,
    facebook_handle: profile.facebook_handle,
    tiktok_handle: profile.tiktok_handle,
    address: profile.address,
    operating_hours: parseOperatingHours(profile.operating_hours),
    price_range: profile.price_range,
    languages: normalizeLanguages(profile.languages),
    business_name: profile.business_name,
    latitude: profile.latitude,
    longitude: profile.longitude,
  });

  const refreshDirty = (): void => {
    const dirty = tracker.isDirty();
    saveBtn.disabled = !dirty;
    dirtyFlag.textContent = dirty ? '• unsaved changes' : '';
  };

  fieldsCol.innerHTML = renderFormHtml(tracker);
  wireFormFields(fieldsCol, tracker, refreshDirty);

  const hoursEl = fieldsCol.querySelector<HTMLElement>('#hours-picker');
  if (hoursEl) {
    mountHoursPicker(hoursEl, tracker.get('operating_hours'), (next) => {
      tracker.set('operating_hours', next);
      refreshDirty();
    });
  }

  const chipsRoot = fieldsCol.querySelector<HTMLElement>('#languages-chips');
  if (chipsRoot) {
    mountLanguagesChips(chipsRoot, tracker.get('languages') ?? [], (next) => {
      tracker.set('languages', next.length === 0 ? null : next);
      refreshDirty();
    });
  }

  // Media sections (logo + cover + gallery). These persist on upload,
  // independent of the text-field Save button. Surfaced directly under the
  // Identity section (not buried at the bottom of the scroll) so the upload
  // controls are discoverable — photos are the highest-value onboarding content.
  const photosEl = document.createElement('section');
  photosEl.className = 'field-section media-section';
  const galleryEl = document.createElement('section');
  galleryEl.className = 'field-section media-section';
  const identitySection = fieldsCol.querySelector<HTMLElement>('form.edit-form .field-section');
  if (identitySection) identitySection.after(photosEl, galleryEl);
  else fieldsCol.append(photosEl, galleryEl);
  // Consent, in plain words, next to the thing it is about (WP5). Providers are
  // handing us images of their premises and staff; "shown to riders in Curate" is
  // the fact they need before they upload, not buried in a policy page.
  const consent = (): HTMLElement => {
    const p = document.createElement('p');
    p.className = 'consent-line';
    p.textContent =
      'Anything you upload here is shown to travellers in the Curate app. '
      + 'Please only add images you own and are happy to publish.';
    return p;
  };
  photosEl.append(consent());
  galleryEl.append(consent());

  initMediaSections({
    userId: session.user.id,
    photosEl,
    galleryEl,
    media: {
      logo_url: profile.logo_url,
      banner_url: profile.banner_url,
      cover_image_url: profile.cover_image_url,
      banner_align_x: profile.banner_align_x,
      banner_align_y: profile.banner_align_y,
      media_urls: toStringArray(profile.media_urls),
      media_types: toMediaTypes(profile.media_types),
    },
  });

  // Per-type fields (WP4). Fetched separately rather than widened into
  // PROFILE_COLUMNS so the identity/media load path above is untouched: the
  // panel is optional, and a failure here must not stop the profile rendering.
  void (async () => {
    const { data: perType, error: perTypeErr } = await supabase
      .from('provider_profiles')
      .select(PER_TYPE_COLUMNS)
      .eq('id', profile.id)
      .maybeSingle();
    if (perTypeErr || !perType) return;
    const perTypeEl = document.createElement('section');
    galleryEl.after(perTypeEl);
    mountPerTypeSection({
      container: perTypeEl,
      profileId: profile.id,
      profile: perType as unknown as PerTypeProfile,
    });
  })();

  // "Add your menu" — food & drink only (pilot scope, decision 061). A bike shop
  // has no menu to photograph, and offering it would just be noise.
  if (isFoodType(profile.business_type)) {
    const menuEl = document.createElement('section');
    galleryEl.after(menuEl);
    mountMenuSection({ container: menuEl, providerId: profile.id, userId: session.user.id });
  }

  const [lng0, lat0] = await getInitialCoords(profile);
  updateCoordsDisplay(coordsDisplay, lng0, lat0);

  const { map } = initEditMap(mapEl, lng0, lat0, (lng, lat) => {
    tracker.set('latitude', lat);
    tracker.set('longitude', lng);
    updateCoordsDisplay(coordsDisplay, lng, lat);
    refreshDirty();
  });

  saveBtn.addEventListener('click', async () => {
    const diff = tracker.diff();
    if (Object.keys(diff).length === 0) return;

    if (diff.operating_hours) {
      const err = validateWeeklyHours(diff.operating_hours);
      if (err) {
        dirtyFlag.textContent = `Invalid hours: ${err}`;
        return;
      }
    }

    if (typeof diff.website === 'string' && diff.website !== '' && !/^https?:\/\//.test(diff.website)) {
      diff.website = `https://${diff.website}`;
      tracker.set('website', diff.website);
      const websiteInput = fieldsCol.querySelector<HTMLInputElement>('#field-website');
      if (websiteInput) websiteInput.value = diff.website;
    }

    if ('business_name' in diff) {
      const ok = window.confirm(
        `Change business name to "${diff.business_name ?? ''}"? This is what customers see in the app.`,
      );
      if (!ok) return;
    }

    // Serialize hours to the flat { "Monday": "9:00 AM - 6:00 PM" | "Closed" }
    // map the apps + in-app editor read (see fields.ts). Everything else writes
    // its column value as-is.
    const payload: Record<string, unknown> = { ...diff };
    if ('operating_hours' in diff) {
      payload.operating_hours = diff.operating_hours
        ? weeklyHoursToDisplayMap(diff.operating_hours)
        : null;
    }

    saveBtn.disabled = true;
    dirtyFlag.textContent = 'Saving…';
    const { error: updErr } = await supabase
      .from('provider_profiles')
      .update(payload)
      .eq('id', profile.id);
    if (updErr) {
      dirtyFlag.textContent = `Save failed: ${updErr.message}`;
      saveBtn.disabled = false;
      return;
    }

    tracker.reset(tracker.getAll());
    dirtyFlag.textContent = `Saved ${new Date().toLocaleTimeString()}`;
    saveBtn.disabled = true;
  });

  signoutBtn.addEventListener('click', () => void signOut());

  window.addEventListener('resize', () => map.resize());
}

function renderShell(profile: ProviderProfileRow): string {
  return `
    <div class="edit-shell">
      <header class="edit-header">
        <div>
          <h1>${esc(profile.business_name)}</h1>
          <p class="muted small">${esc(profile.business_type ?? '(no type set)')}</p>
        </div>
        <div class="edit-header-actions">
          <span id="dirty-flag" class="muted small" aria-live="polite"></span>
          <button id="save-btn" class="primary" disabled>Save</button>
          <button id="signout-btn" class="link">Sign out</button>
        </div>
      </header>
      <div class="edit-body">
        <div class="edit-map-col">
          <div id="map" class="map"></div>
          <p class="map-hint muted small">Drag the pin to where customers actually find you. The street view zooms automatically.</p>
          <p class="muted small">Coords: <code id="coords-display"></code></p>
        </div>
        <aside class="edit-fields-col"></aside>
      </div>
    </div>
  `;
}

function renderFormHtml(tracker: DirtyTracker<EditableProfileFields>): string {
  const v = (k: keyof EditableProfileFields): string => {
    const raw = tracker.get(k);
    if (typeof raw === 'string') return esc(raw);
    return '';
  };
  const taglineLen = (tracker.get('tagline') ?? '').length;
  const currentPrice = tracker.get('price_range');

  return `
    <form class="edit-form" autocomplete="off">
      <section class="field-section">
        <h2 class="field-section-title">Identity</h2>
        <label for="field-business_name">Business name</label>
        <input id="field-business_name" type="text" value="${v('business_name')}" />
        <p class="muted small">Customers see this in the app — confirm before saving.</p>

        <label for="field-tagline">Tagline <span class="muted small">— one short line above the description on tour cards</span></label>
        <input id="field-tagline" type="text" maxlength="${TAGLINE_MAX}" placeholder="e.g. Lampredotto since 1953" value="${v('tagline')}" />
        <p class="muted small char-count" data-for="field-tagline">${taglineLen} / ${TAGLINE_MAX}</p>

        <label for="field-description">Description</label>
        <textarea id="field-description" rows="6" placeholder="Tell visitors what makes you worth their time.">${v('description')}</textarea>
      </section>

      <section class="field-section">
        <h2 class="field-section-title">Contact</h2>
        <label for="field-email">Email</label>
        <input id="field-email" type="email" value="${v('email')}" />

        <label for="field-phone">Phone</label>
        <input id="field-phone" type="tel" value="${v('phone')}" />

        <label for="field-website">Website</label>
        <input id="field-website" type="url" placeholder="https://…" value="${v('website')}" />
      </section>

      <section class="field-section">
        <h2 class="field-section-title">Social</h2>
        <label for="field-instagram_handle">Instagram</label>
        <input id="field-instagram_handle" type="text" placeholder="@handle" value="${v('instagram_handle')}" />

        <label for="field-facebook_handle">Facebook</label>
        <input id="field-facebook_handle" type="text" value="${v('facebook_handle')}" />

        <label for="field-tiktok_handle">TikTok</label>
        <input id="field-tiktok_handle" type="text" placeholder="@handle" value="${v('tiktok_handle')}" />
      </section>

      <section class="field-section">
        <h2 class="field-section-title">Location</h2>
        <label for="field-address">Street address</label>
        <input id="field-address" type="text" value="${v('address')}" />
        <p class="muted small">The pin on the map is the source of truth; this is the text we display.</p>
      </section>

      <section class="field-section">
        <h2 class="field-section-title">Practical</h2>

        <label>Opening hours</label>
        <div id="hours-picker"></div>

        <label for="field-price_range">Price range</label>
        <select id="field-price_range">
          <option value="">(unset)</option>
          ${PRICE_RANGES.map((p) => `<option value="${p}"${currentPrice === p ? ' selected' : ''}>${p}</option>`).join('')}
        </select>

        <label>Languages</label>
        <div id="languages-chips"></div>
      </section>
    </form>
  `;
}

const TEXT_FIELD_KEYS = [
  'business_name', 'tagline', 'description',
  'email', 'phone', 'website',
  'instagram_handle', 'facebook_handle', 'tiktok_handle',
  'address',
] as const satisfies ReadonlyArray<keyof EditableProfileFields>;

function wireFormFields(
  root: HTMLElement,
  tracker: DirtyTracker<EditableProfileFields>,
  onChange: () => void,
): void {
  const form = root.querySelector<HTMLFormElement>('form.edit-form');
  if (form) form.addEventListener('submit', (e) => e.preventDefault());

  for (const key of TEXT_FIELD_KEYS) {
    const el = root.querySelector<HTMLInputElement | HTMLTextAreaElement>(`#field-${key}`);
    if (!el) continue;
    el.addEventListener('input', () => {
      const raw = el.value;
      if (key === 'business_name') {
        tracker.set('business_name', raw);
      } else {
        const value = raw === '' ? null : raw;
        tracker.set(key, value as EditableProfileFields[typeof key]);
      }
      if (key === 'tagline') {
        const counter = root.querySelector<HTMLElement>('.char-count[data-for="field-tagline"]');
        if (counter) counter.textContent = `${raw.length} / ${TAGLINE_MAX}`;
      }
      onChange();
    });
  }

  const priceSelect = root.querySelector<HTMLSelectElement>('#field-price_range');
  if (priceSelect) {
    priceSelect.addEventListener('change', () => {
      tracker.set('price_range', priceSelect.value === '' ? null : priceSelect.value);
      onChange();
    });
  }
}

function mountLanguagesChips(
  root: HTMLElement,
  initial: string[],
  onChange: (next: string[]) => void,
): void {
  let state = [...initial];

  const emit = (): void => onChange([...state]);

  const render = (): void => {
    root.innerHTML = '';

    const chips = document.createElement('div');
    chips.className = 'chips';
    for (const lang of state) {
      const chip = document.createElement('span');
      chip.className = 'chip';
      chip.textContent = lang;
      const x = document.createElement('button');
      x.type = 'button';
      x.textContent = '×';
      x.setAttribute('aria-label', `Remove ${lang}`);
      x.addEventListener('click', () => {
        state = state.filter((l) => l !== lang);
        render();
        emit();
      });
      chip.appendChild(x);
      chips.appendChild(chip);
    }
    root.appendChild(chips);

    const addRow = document.createElement('div');
    addRow.className = 'chip-add';

    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = 'Add a language…';
    input.setAttribute('list', 'language-suggestions');

    const datalist = document.createElement('datalist');
    datalist.id = 'language-suggestions';
    for (const l of COMMON_LANGUAGES) {
      const opt = document.createElement('option');
      opt.value = l;
      datalist.appendChild(opt);
    }

    const addBtn = document.createElement('button');
    addBtn.type = 'button';
    addBtn.className = 'secondary small';
    addBtn.textContent = 'Add';

    const tryAdd = (): void => {
      const v = input.value.trim();
      if (!v) return;
      if (state.includes(v)) {
        input.value = '';
        return;
      }
      state.push(v);
      input.value = '';
      render();
      emit();
      const refocus = root.querySelector<HTMLInputElement>('.chip-add input');
      if (refocus) refocus.focus();
    };
    addBtn.addEventListener('click', tryAdd);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        tryAdd();
      }
    });

    addRow.append(input, addBtn, datalist);
    root.appendChild(addRow);
  };

  render();
}

function updateCoordsDisplay(el: HTMLElement, lng: number, lat: number): void {
  el.textContent = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
}

function renderNoProfile(container: HTMLElement): void {
  container.innerHTML = `
    <main class="container narrow">
      <h1>No profile found</h1>
      <p>Your account doesn't have a provider profile yet. If you've just been invited and weren't expecting this,
      contact <a href="mailto:john@ebikesflorence.com">john@ebikesflorence.com</a>.</p>
      <button id="signout-btn">Sign out</button>
    </main>
  `;
  const signoutBtn = container.querySelector<HTMLButtonElement>('#signout-btn');
  if (signoutBtn) signoutBtn.addEventListener('click', () => void signOut());
}

async function getInitialCoords(profile: ProviderProfileRow): Promise<[number, number]> {
  if (profile.latitude !== null && profile.longitude !== null) {
    return [profile.longitude, profile.latitude];
  }
  if (profile.city_id) {
    const { data } = await supabase
      .from('cities')
      .select('latitude, longitude')
      .eq('id', profile.city_id)
      .single<{ latitude: number | null; longitude: number | null }>();
    if (data?.latitude != null && data?.longitude != null) {
      return [data.longitude, data.latitude];
    }
  }
  return FLORENCE_CENTROID;
}
